import { proxy } from 'valtio';

import { createErrorClass } from '@canvas/error';

export const indexeddbStatus = proxy({
  // We always assume that indexeddb begins healthy
  healthy: true,
});

export const updateIndexeddbStatus = (healthy: boolean) => {
  indexeddbStatus.healthy = healthy;
};

export const IndexedDBTimeoutError = createErrorClass('IndexedDBTimeoutError');

const isTest = process.env.MODE === 'test' || process.env.NODE_ENV === 'test';

let timeoutEnabledInTests = false;

/**
 * Enable timeout behavior in test environments.
 * By default, timeouts are disabled in test environments to avoid flaky tests.
 * Call this function in tests that specifically need to test timeout behavior.
 */
export function enableTimeoutInTests() {
  timeoutEnabledInTests = true;
}

/**
 * Disable timeout behavior in test environments (default behavior).
 */
export function disableTimeoutInTests() {
  timeoutEnabledInTests = false;
}

/**
 * Helper to wrap a promise with a timeout which rejects after the timeoutMs has elapsed, but notably
 * does not yeject directly
 * @param timeoutMs - The timeout in milliseconds.
 * @param onTimeout - The function to call when the timeout is reached.
 * @param operation - The operation to wrap with the timeout. In practice, this is aimed to be used to
 * report indexedDB timeouts, so it should have its behaivour configured through dependency injection
 * @returns A promise that will resolve with the result of the operation when it completes, even if the timeout fires first.
 * The promise will only hang forever if the operation never completes.
 */
export function withTimeout<T>(
  timeoutMs: number,
  onTimeout: (error: Error) => void
): (operation: () => Promise<T>) => Promise<T> {
  return (operation) => {
    // In test environments, disable timeout by default unless explicitly enabled
    if (isTest && !timeoutEnabledInTests) {
      return operation();
    }

    return new Promise<T>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const clearTimeoutIfNeeded = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      timeoutId = setTimeout(() => {
        const error = new IndexedDBTimeoutError('IndexedDB operation timed out');
        onTimeout(error);
      }, timeoutMs);

      operation().then(
        (result) => {
          clearTimeoutIfNeeded();
          resolve(result);
        },
        (error) => {
          clearTimeoutIfNeeded();
          reject(error);
        }
      );
    });
  };
}
